import { useState, useCallback } from 'react';
import { produksjonAPI, PlanElement, Produksjon } from '@/services/api';

/**
 * Custom hook for managing plan elements CRUD operations
 * 
 * Extracted from PlanEditor.tsx to separate data layer from presentation.
 * Handles fetching, creating, updating, and deleting plan elements.
 */

interface UsePlanElementsProps {
  produksjonId: number;
  onSave?: () => void;
}

export const usePlanElements = ({ produksjonId, onSave }: UsePlanElementsProps) => {
  const [elements, setElements] = useState<PlanElement[]>([]);
  const [produksjon, setProduksjon] = useState<Produksjon | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  /**
   * Fetch both production and plan elements
   */
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [prodData, planData] = await Promise.all([
        produksjonAPI.getById(produksjonId),
        produksjonAPI.getPlanElementer(produksjonId)
      ]);
      
      setProduksjon(prodData.produksjon);
      setElements(planData.planElementer);
    } catch (err: any) {
      console.error('Fetch error:', err);
      setError('Kunne ikke laste data');
    } finally {
      setLoading(false);
    }
  }, [produksjonId]);

  /**
   * Fetch only plan elements (lighter operation)
   */
  const fetchPlanElementer = useCallback(async () => {
    try {
      setError(null);
      const data = await produksjonAPI.getPlanElementer(produksjonId);
      setElements(data.planElementer);
    } catch (err: any) {
      console.error('Fetch plan-elementer error:', err);
      setError('Kunne ikke laste planinnslag');
    }
  }, [produksjonId]);

  /**
   * Create a new plan element
   */
  const createElement = useCallback(async (
    type: 'overskrift' | 'hendelse' | 'sang',
    data: {
      navn: string;
      varighetMinutter?: number;
      førProduksjonsstart?: boolean;
      parentId?: number;
      rekkefølge: number;
      beskrivelse?: string;
      musikkId?: number;
      musikkTransponeringId?: number | null;
    }
  ) => {
    try {
      setError(null);
      
      const payload = {
        type,
        navn: data.navn.trim(),
        ...(type === 'hendelse' && data.parentId !== undefined && { 
          varighetMinutter: data.varighetMinutter,
          parentId: data.parentId 
        }),
        ...(type === 'sang' && data.parentId !== undefined && { 
          parentId: data.parentId,
          musikkId: data.musikkId,
          musikkTransponeringId: data.musikkTransponeringId || undefined,
        }),
        ...(type === 'overskrift' && { førProduksjonsstart: data.førProduksjonsstart }),
        rekkefølge: data.rekkefølge,
        beskrivelse: data.beskrivelse,
      };
      
      await produksjonAPI.addPlanElement(produksjonId, payload);
      
      setSuccess(`${type === 'overskrift' ? 'Overskrift' : type === 'sang' ? 'Sang' : 'Innslag'} lagt til`);
      await fetchPlanElementer();
      if (onSave) onSave();
      
      setTimeout(() => setSuccess(null), 3000);
      return true;
    } catch (err: any) {
      console.error('Create error:', err);
      setError(err.response?.data?.error || 'Kunne ikke opprette');
      return false;
    }
  }, [produksjonId, fetchPlanElementer, onSave]);

  /**
   * Update an existing plan element
   */
  const updateElement = useCallback(async (
    elementId: number,
    type: 'overskrift' | 'hendelse' | 'sang' | 'musikk_placeholder',
    data: {
      navn?: string;
      varighetMinutter?: number;
      førProduksjonsstart?: boolean;
      beskrivelse?: string;
      parentId?: number;
      rekkefølge?: number;
      musikkTransponeringId?: number | null;
    }
  ) => {
    try {
      setError(null);
      
      await produksjonAPI.updatePlanElement(produksjonId, elementId, {
        ...(data.navn !== undefined && { navn: data.navn.trim() }),
        ...(type === 'hendelse' && data.varighetMinutter !== undefined && { 
          varighetMinutter: data.varighetMinutter 
        }),
        ...(type === 'musikk_placeholder' && data.varighetMinutter !== undefined && { 
          varighetMinutter: data.varighetMinutter 
        }),
        ...(type === 'sang' && data.varighetMinutter !== undefined && { 
          varighetMinutter: data.varighetMinutter 
        }),
        ...(type === 'overskrift' && data.førProduksjonsstart !== undefined && { 
          førProduksjonsstart: data.førProduksjonsstart 
        }),
        ...(type === 'sang' && data.musikkTransponeringId !== undefined && { 
          musikkTransponeringId: data.musikkTransponeringId 
        }),
        ...(data.beskrivelse !== undefined && { 
          beskrivelse: data.beskrivelse 
        }),
        ...(data.parentId !== undefined && { parentId: data.parentId }),
        ...(data.rekkefølge !== undefined && { rekkefølge: data.rekkefølge }),
      });
      
      setSuccess(type === 'sang' ? 'Sang oppdatert' : 'Innslag oppdatert');
      await fetchPlanElementer();
      if (onSave) onSave();
      
      setTimeout(() => setSuccess(null), 3000);
      return true;
    } catch (err: any) {
      console.error('Update error:', err);
      setError(err.response?.data?.error || 'Kunne ikke oppdatere');
      return false;
    }
  }, [produksjonId, fetchPlanElementer, onSave]);

  /**
   * Delete a plan element
   */
  const deleteElement = useCallback(async (elementId: number) => {
    try {
      setError(null);
      await produksjonAPI.removePlanElement(produksjonId, elementId);
      setSuccess('Innslag slettet');
      await fetchPlanElementer();
      if (onSave) onSave();
      setTimeout(() => setSuccess(null), 3000);
      return true;
    } catch (err: any) {
      console.error('Delete error:', err);
      setError('Kunne ikke slette innslag');
      return false;
    }
  }, [produksjonId, fetchPlanElementer, onSave]);

  /**
   * Update rekkefølge for multiple elements (used in drag-and-drop)
   */
  const updateMultipleRekkefølge = useCallback(async (
    updates: Array<{ id: number; rekkefølge: number; parentId?: number }>
  ) => {
    try {
      setError(null);
      
      await Promise.all(
        updates.map(({ id, rekkefølge, parentId }) =>
          produksjonAPI.updatePlanElement(produksjonId, id, {
            rekkefølge,
            ...(parentId !== undefined && { parentId }),
          })
        )
      );
      
      await fetchPlanElementer();
      if (onSave) onSave();
      return true;
    } catch (err: any) {
      console.error('Update rekkefølge error:', err);
      setError('Kunne ikke oppdatere rekkefølge');
      await fetchPlanElementer(); // Rollback on error
      return false;
    }
  }, [produksjonId, fetchPlanElementer, onSave]);

  /**
   * Optimistically update elements (for immediate UI feedback)
   */
  const setElementsOptimistic = useCallback((updater: (prev: PlanElement[]) => PlanElement[]) => {
    setElements(updater);
  }, []);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => setError(null), []);

  /**
   * Clear success message
   */
  const clearSuccess = useCallback(() => setSuccess(null), []);

  return {
    // State
    elements,
    produksjon,
    loading,
    error,
    success,
    
    // Actions
    fetchData,
    fetchPlanElementer,
    createElement,
    updateElement,
    deleteElement,
    updateMultipleRekkefølge,
    setElementsOptimistic,
    clearError,
    clearSuccess,
    setError,
    setSuccess,
  };
};

